import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe, ArrowRight } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/programs", key: "programs" },
  { to: "/house-of-wisdom", key: "knowledge" },
  { to: "/opportunities", key: "opportunities" },
  { to: "/collaborate", key: "collaborate" },
  { to: "/professionals", key: "professionals" },
  { to: "/contact", key: "contact" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isAr = i18n.language?.startsWith("ar");
  const toggleLang = () => i18n.changeLanguage(isAr ? "en" : "ar");

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ivory/90 backdrop-blur-xl border-b border-line shadow-[0_10px_30px_-24px_rgba(27,38,32,0.35)]" : "bg-ivory/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 h-[76px] flex items-center justify-between gap-6">
        <Logo compact />

        <nav className="hidden xl:flex items-center justify-center gap-4 2xl:gap-7 flex-1 min-w-0 px-6" data-testid="nav-desktop">
          {NAV.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.key}-link`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle-button"
            aria-label="Toggle language"
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-line bg-white/60 text-[12px] font-semibold text-ink/80 hover:text-forest hover:border-gold transition-colors"
          >
            <Globe className="h-3.5 w-3.5" />
            {isAr ? "EN" : "AR"}
          </button>
          <div className="hidden md:block">
            <Link to="/join" data-testid="nav-join-cta" className="btn-forest !py-2.5 !px-5 !text-[11px]">
              {t("nav.join")}
              <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
            </Link>
          </div>
          <button
            className="xl:hidden p-2 -mr-1 text-ink"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? t("nav.close") : t("nav.menu")}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-ivory/98 backdrop-blur-xl border-t border-line" data-testid="nav-mobile-panel">
          <nav className="max-w-[1500px] mx-auto px-6 py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-mobile-${n.key}-link`}
                className={({ isActive }) =>
                  `py-3 text-[15px] font-semibold border-b border-line last:border-b-0 transition-colors ${isActive ? "text-forest" : "text-ink/80"}`
                }
              >
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
            <Link to="/join" data-testid="nav-mobile-join-cta" className="btn-forest mt-5 self-start">
              {t("nav.join")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
